// ── EventsTable ───────────────────────────────────────────────────────────────
import type { ReactNode } from "react";
import type { Job, BackupFile, Connector, Schedule } from "../pages/Dashboard";

interface EventsTableProps {
  jobs: Job[];
  onSelect: (job: Job) => void;
}

export function EventsTable({ jobs, onSelect }: EventsTableProps) {
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Time</th>
          <th>Status</th>
          <th>Records</th>
          <th>Size</th>
          <th>Duration</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {jobs.length === 0 && (
          <tr><td colSpan={6} className="td-empty">No backups yet</td></tr>
        )}
        {jobs.map((job) => {
          const time = new Date(job.queued_at).toLocaleTimeString("en-GB", {
            hour: "2-digit", minute: "2-digit",
          });
          const totalRecords = job.total_records
            ? Object.values(job.total_records).reduce((a, b) => a + b, 0)
            : 0;
          const duration = job.started_at && job.completed_at
            ? `${Math.round((new Date(job.completed_at).getTime() - new Date(job.started_at).getTime()) / 1000)}s`
            : job.status === "running" ? "…" : "—";

          return (
            <tr key={job.id} onClick={() => onSelect(job)} className="tr-clickable">
              <td className="td-mono">{time}</td>
              <td><StatusBadge status={job.status} /></td>
              <td className="td-mono">{totalRecords > 0 ? totalRecords.toLocaleString() : "—"}</td>
              <td className="td-mono">{job.total_bytes ? formatBytes(job.total_bytes) : "—"}</td>
              <td className="td-mono">{duration}</td>
              <td>
                <button className="expand-btn" onClick={(e) => { e.stopPropagation(); onSelect(job); }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── FilesTable ────────────────────────────────────────────────────────────────
export function FilesTable({ job }: { job: Job | null }) {
  if (!job?.files?.length) {
    return (
      <div className="td-empty" style={{ padding: "24px 20px" }}>
        {job ? "No files for this job" : "Select a job to view files"}
      </div>
    );
  }
  return (
    <table className="data-table">
      <thead>
        <tr><th>File</th><th>Type</th><th>Records</th><th>Size</th></tr>
      </thead>
      <tbody>
        {job.files.map((f) => (
          <tr key={f.id}>
            <td className="td-mono">{f.storage_key.split("/").pop()}</td>
            <td><span className="badge badge-neutral">{f.data_type}</span></td>
            <td className="td-mono">{f.record_count.toLocaleString()}</td>
            <td className="td-mono">{formatBytes(f.size_bytes)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────
interface StatCardProps {
  accent: "teal" | "amber" | "blue";
  label: string;
  value: string;
  sub: React.ReactNode;
  icon: string;
}

export function StatCard({ accent, label, value, sub }: StatCardProps) {
  return (
    <div className={`stat-card stat-card--${accent}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

// ── ConnectorsPanel ───────────────────────────────────────────────────────────
export function ConnectorsPanel({ connectors }: { connectors: Connector[] }) {
  return (
    <div className="card">
      <div className="card-head">
        <h2>Connectors</h2>
        <button className="btn btn-ghost btn-sm">+ Add</button>
      </div>
      <div className="card-body">
        {connectors.map((c) => {
          const typeName = c.connector_type === "freeagent" ? "FreeAgent"
                         : c.connector_type === "xero" ? "Xero"
                         : c.connector_type;
          return (
            <div key={c.id} className="connector-item">
              <div className="connector-logo" style={c.connector_type === "freeagent" ? { background: "none", padding: 0, overflow: "hidden" } : undefined}>
                {c.connector_type === "freeagent" ? (
                  <img src="/freeagent-logo.svg" alt="FreeAgent" style={{ width: 32, height: 32 }} />
                ) : c.connector_type === "xero" ? (
                  <span style={{ fontWeight: 700, color: "#13B5EA" }}>X</span>
                ) : (
                  c.connector_type[0].toUpperCase()
                )}
              </div>
              <div className="connector-info">
                <div className="connector-name">{c.display_name || typeName}</div>
                <div className="connector-status">
                  <span className="status-dot" />
                  {c.is_active ? "connected" : "inactive"}
                </div>
              </div>
            </div>
          );
        })}
        {connectors.length === 0 && (
          <div className="td-empty" style={{ padding: "16px 20px", fontSize: 13 }}>
            No connectors yet — add one to start backing up
          </div>
        )}
      </div>
    </div>
  );
}

// ── StoragePanel ──────────────────────────────────────────────────────────────
const PLAN_BYTES: Record<string, number> = {
  free:       1  * 1024 * 1024 * 1024,   // 1 GB
  sme:        10 * 1024 * 1024 * 1024,   // 10 GB
  practice:   100 * 1024 * 1024 * 1024,  // 100 GB
  enterprise: 1024 * 1024 * 1024 * 1024, // 1 TB
};

export function StoragePanel({ usedBytes, schedules }: { usedBytes: number; schedules: Schedule[] }) {
  const plan        = "sme"; // TODO: pull from tenant
  const planBytes   = PLAN_BYTES[plan];
  const pct         = Math.min(100, Math.round((usedBytes / planBytes) * 100));
  const schedule    = schedules[0];

  return (
    <div className="card">
      <div className="card-head"><h2>Storage</h2></div>
      <div className="card-body" style={{ padding: "14px 20px" }}>
        <div className="storage-row">
          <span style={{ fontSize: 13, color: "var(--text2)" }}>Used</span>
          <span className="td-mono" style={{ fontWeight: 500 }}>{formatBytes(usedBytes)}</span>
        </div>
        <div className="storage-bar-bg">
          <div className="storage-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="storage-nums">
          <span>0</span>
          <span style={{ color: "var(--text3)", fontSize: 11 }}>S3-compatible · {import.meta.env.VITE_STORAGE_REGION ?? "eu-west-1"}</span>
          <span>{formatBytes(planBytes)} plan</span>
        </div>
        {schedule && (
          <>
            <div className="schedule-row">
              <span className="schedule-label">Retention</span>
              <span className="schedule-val">{schedule.retention_days} days</span>
            </div>
            <div className="schedule-row">
              <span className="schedule-label">Schedule</span>
              <span className="schedule-val">{schedule.cron_expression}</span>
            </div>
            <div className="schedule-row">
              <span className="schedule-label">Data types</span>
              <span className="schedule-val">{schedule.data_types.length} types</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── PageError ─────────────────────────────────────────────────────────────────
interface PageErrorProps {
  message?: string | null;
  onRetry?: () => void;
}

export function PageError({ message, onRetry }: PageErrorProps) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "60px 24px", textAlign: "center",
    }}>
      <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.35 }}>⚠</div>
      <p style={{ fontSize: 15, fontWeight: 500, color: "var(--text2)", marginBottom: 6 }}>
        Something went wrong
      </p>
      <p style={{ fontSize: 13, color: "var(--text3)", marginBottom: 20, maxWidth: 380 }}>
        {message ?? "An unexpected error occurred. Please try again."}
      </p>
      {onRetry && (
        <button className="btn btn-ghost btn-sm" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

// ── friendlyError — maps ApiError/network errors to user-readable messages ────
export function friendlyError(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message;
    if (msg.includes("401") || msg.toLowerCase().includes("unauthori")) {
      return "Your session has expired. Please refresh the page and log in again.";
    }
    if (msg.includes("403") || msg.toLowerCase().includes("forbidden")) {
      return "You don't have permission to view this.";
    }
    if (msg.includes("404")) return "The requested data could not be found.";
    if (msg.includes("429")) return "Too many requests. Please wait a moment and try again.";
    if (msg.includes("5"))   return "A server error occurred. Please try again in a moment.";
    if (msg.toLowerCase().includes("failed to fetch") || msg.toLowerCase().includes("network")) {
      return "Unable to connect to the server. Please check your internet connection.";
    }
    return msg;
  }
  return "An unexpected error occurred.";
}

// ── Topbar ────────────────────────────────────────────────────────────────────
interface TopbarProps {
  title: string;
  subtitle: string;
  onBackup?: () => void;
  backupLoading?: boolean;
  action?: ReactNode;
}

export function Topbar({ title, subtitle, onBackup, backupLoading, action }: TopbarProps) {
  return (
    <div className="topbar">
      <div className="topbar-left">
        <h1>{title}</h1>
        <p className="topbar-sub">{subtitle}</p>
      </div>
      <div className="topbar-right">
        {action}
        {onBackup && (
          <button className="btn btn-ghost" onClick={onBackup} disabled={backupLoading}>
            {backupLoading ? "Running…" : "Run backup now"}
          </button>
        )}
        <div className="avatar">JD</div>
      </div>
    </div>
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
export function StatusBadge({ status }: { status: string }) {
  const cls = {
    complete:  "badge-success",
    failed:    "badge-fail",
    running:   "badge-running",
    queued:    "badge-neutral",
    cancelled: "badge-neutral",
  }[status] ?? "badge-neutral";
  return <span className={`badge ${cls}`}>{status}</span>;
}

// ── Shared helpers ────────────────────────────────────────────────────────────
function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}